/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'preact/compat';
import { Container, Slot, SlotProps, classes } from '@adobe-commerce/elsie/lib';

import {
  CartSummaryList as CartSummaryListComponent,
  EmptyCart,
} from '@/cart/components';

import { getPersistedCartData } from '@/cart/lib/persisted-data';
import { decodeHTMLEntities } from '@/cart/lib/decodeHTMLEntities';
import { CartModel } from '@/cart/data/models/cart-model';
import {
  Accordion,
  AccordionSection,
  Button,
  CartItem,
  CartList,
  Icon,
  Image,
  ImageProps,
  InLineAlert,
  Price,
  PriceProps,
  Incrementer,
} from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { VNode } from 'preact';
import { events, Cart } from '@adobe-commerce/event-bus';
import { state } from '@/cart/lib/state';
import {
  ChevronDown,
  ChevronRight,
  OrderError,
  WarningWithCircle,
  Trash,
} from '@adobe-commerce/elsie/icons';
import { updateProductsFromCart } from '@/cart/api';
import { addProductsToCart } from '@/cart/api/addProductsToCart/addProductsToCart';

export interface CartSummaryListProps extends HTMLAttributes<HTMLDivElement> {
  hideHeading?: boolean;
  hideFooter?: boolean;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  itemTitleHeadingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  routeProduct?: (item: CartModel['items'][0]) => string;
  routeEmptyCartCTA?: () => string;
  routeCart?: () => string;
  onItemUpdate?: ({ item }: { item: CartModel['items'][0] }) => void;
  onItemRemove?: ({ item }: { item: CartModel['items'][0] }) => void;
  maxItems?: number; // Maximum number of items to display
  showMaxItems?: boolean;
  attributesToHide?: SwitchableAttributes[]; // Product attributes to hide
  slots?: {
    Heading?: SlotProps;
    EmptyCart?: SlotProps;
    Footer?: SlotProps;
    RowTotalFooter?: SlotProps<{ item: CartModel['items'][number] }>;
    Thumbnail?: SlotProps<{
      item: CartModel['items'][number];
      defaultImageProps: ImageProps;
    }>;
    ProductAttributes?: SlotProps;
    CartSummaryFooter?: SlotProps;
    CartItem?: SlotProps;
    UndoBanner?: SlotProps<{
      item: CartModel['items'][0];
      loading: boolean;
      error?: string;
      onUndo: () => void;
      onDismiss: () => void;
    }>;
    ConfirmDeleteBanner?: SlotProps<{
      item: CartModel['items'][0];
      loading: boolean;
      onConfirm: () => void;
      onCancel: () => void;
    }>;
    ItemTitle?: SlotProps<{ item: CartModel['items'][number] }>;
    ItemPrice?: SlotProps<{ item: CartModel['items'][number] }>;
    ItemQuantity?: SlotProps<{
      item: CartModel['items'][number];
      enableUpdateItemQuantity: boolean | { removeOnZero?: boolean };
      handleItemQuantityUpdate: (
        item: CartModel['items'][number],
        quantity: number
      ) => void;
      itemsLoading: Set<string>;
      handleItemsError: (uid: string, message?: string) => void;
      handleItemsLoading: (uid: string, state: boolean) => void;
      onItemUpdate?: ({ item }: { item: CartModel['items'][number] }) => void;
    }>;
    ItemTotal?: SlotProps<{ item: CartModel['items'][number] }>;
    ItemSku?: SlotProps<{ item: CartModel['items'][number] }>;
    ItemRemoveAction?: SlotProps<{
      item: CartModel['items'][number];
      enableRemoveItem: boolean;
      handleItemQuantityUpdate: (
        item: CartModel['items'][number],
        quantity: number
      ) => void;
      handleItemsError: (uid: string, message?: string) => void;
      handleItemsLoading: (uid: string, state: boolean) => void;
      onItemUpdate?: ({ item }: { item: CartModel['items'][number] }) => void;
      itemsLoading: Set<string>;
    }>;
  };
  enableRemoveItem?: boolean;
  enableUpdateItemQuantity?: boolean | { removeOnZero?: boolean };
  onItemsErrorsChange?: (errors: Map<string, string>) => void;
  accordion?: boolean;
  variant?: 'primary' | 'secondary';
  isLoading?: boolean;
  showDiscount?: boolean;
  showSavings?: boolean;
  quantityType?: 'stepper' | 'dropdown';
  dropdownOptions?: { value: string; text: string }[];
  undo?: boolean;
  confirmBeforeDelete?: boolean;
  includeOutOfStockItems?: boolean;
  /**
   * TEST ONLY: Allows test to inject recentlyRemovedItems for coverage
   */
  __testRecentlyRemovedItems?: Array<{
    item: CartModel['items'][0];
    index: number;
    loading: boolean;
    error?: string;
    beingRemoved?: boolean;
  }>;
}

// Attributes that can be hidden or shown
export type SwitchableAttributes =
  | 'name'
  | 'image'
  | 'configurations'
  | 'warning'
  | 'alert'
  | 'sku'
  | 'price'
  | 'quantity'
  | 'total'
  | 'totalDiscount'
  | 'totalExcludingTax';

// Ref interface for exposing functions to tests
export interface CartSummaryListRef {
  handleUndo: (uid: string) => Promise<void>;
}

export const CartSummaryList: Container<
  CartSummaryListProps,
  CartModel | null
> = forwardRef<
  CartSummaryListRef,
  CartSummaryListProps & { initialData?: CartModel | null }
>(
  (
    {
      initialData = null,
      hideHeading,
      hideFooter,
      headingLevel = 1,
      itemTitleHeadingLevel,
      routeProduct,
      routeEmptyCartCTA,
      routeCart,
      onItemUpdate,
      onItemRemove,
      maxItems, //if maxItems is not provided, default to show all items
      slots,
      attributesToHide = [],
      enableRemoveItem,
      enableUpdateItemQuantity,
      onItemsErrorsChange,
      accordion = false,
      variant = 'primary',
      isLoading,
      showMaxItems,
      showDiscount,
      showSavings,
      quantityType,
      dropdownOptions,
      undo = false,
      confirmBeforeDelete = false,
      includeOutOfStockItems = false,
      __testRecentlyRemovedItems,
      ...props
    },
    ref
  ) => {
    const [loading, setLoading] = useState(!initialData);

    const [cartModelData, setCartModelData] = useState<CartModel | null>(
      initialData
    );
    const [itemsLoading, setItemLoading] = useState<Set<string>>(new Set());

    const [itemsErrors, setItemErrors] = useState<Map<string, string>>(
      new Map()
    );

    const [pendingDeleteUids, setPendingDeleteUids] = useState<Set<string>>(
      new Set()
    );

    const [recentlyRemovedItems, setRecentlyRemovedItems] = useState<
      {
        item: CartModel['items'][0];
        index: number;
        loading: boolean;
        error?: string;
        beingRemoved?: boolean;
      }[]
    >(__testRecentlyRemovedItems || []);

    // TEST ONLY: allow test to update recentlyRemovedItems after mount
    useEffect(() => {
      if (
        process.env.NODE_ENV === 'test' &&
        __testRecentlyRemovedItems &&
        recentlyRemovedItems !== __testRecentlyRemovedItems
      ) {
        setRecentlyRemovedItems(__testRecentlyRemovedItems);
      }
    }, [__testRecentlyRemovedItems, recentlyRemovedItems]);

    const removeOnZero =
      typeof enableUpdateItemQuantity === 'object'
        ? enableUpdateItemQuantity.removeOnZero ?? false
        : false;

    const cartTaxesConfig = state.config?.shoppingCartDisplaySetting;

    const [displayMaxItems, setDisplayMaxItems] = useState<boolean>(
      showMaxItems ? true : !maxItems && !showMaxItems
    );

    const dictionary = useText({
      file: 'Cart.CartItem.file',
      files: 'Cart.CartItem.files',
      heading: 'Cart.Cart.heading',
      message: 'Cart.CartItem.message',
      recipient: 'Cart.CartItem.recipient',
      regularPrice: 'Cart.CartItem.regularPrice',
      discountedPrice: 'Cart.CartItem.discountedPrice',
      sender: 'Cart.CartItem.sender',
      lowInventory: 'Cart.CartItem.lowInventory',
      insufficientQuantity: 'Cart.CartItem.insufficientQuantity',
      insufficientQuantityGeneral: 'Cart.CartItem.insufficientQuantityGeneral',
      outOfStockHeading: 'Cart.OutOfStockMessage.heading',
      outOfStockDescription: 'Cart.OutOfStockMessage.message',
      outOfStockAlert: 'Cart.OutOfStockMessage.alert',
      removeAction: 'Cart.OutOfStockMessage.action',
      notAvailableMessage: 'Cart.CartItem.notAvailableMessage',
      viewMore: 'Cart.Cart.viewMore',
      viewAll: 'Cart.Cart.viewAll',
      discountPercent: 'Cart.CartItem.discountPercentage',
      savingsAmount: 'Cart.CartItem.savingsAmount',
      itemBeingRemoved: 'Cart.CartItem.itemBeingRemoved',
      itemRemoved: 'Cart.CartItem.itemRemoved',
      itemRemovedDescription: 'Cart.CartItem.itemRemovedDescription',
      undoAction: 'Cart.CartItem.undoAction',
      dismissAction: 'Cart.CartItem.dismissAction',
      confirmDeleteHeading: 'Cart.CartItem.confirmDeleteHeading',
      confirmAction: 'Cart.CartItem.confirmAction',
      cancelAction: 'Cart.CartItem.cancelAction',
      remove: 'Dropin.CartItem.remove.label',
      removeDefault: 'Dropin.CartItem.removeDefault.label',
      quantity: 'Dropin.CartItem.quantity.label',
    });

    const handleItemsLoading = (uid: string, state: boolean) => {
      setItemLoading((prev) => {
        state ? prev.add(uid) : prev.delete(uid);
        return new Set(prev);
      });
    };

    const handleItemsError = (uid: string, message?: string) => {
      setItemErrors((prev) => {
        message ? prev.set(uid, message) : prev.delete(uid);
        return new Map(prev);
      });
    };

    const handlePendingDelete = (item: CartModel['items'][0]) => {
      if (confirmBeforeDelete) {
        setPendingDeleteUids((prev) => new Set(prev).add(item.uid));
      } else {
        handleItemQuantityUpdate(item, 0, true);
      }
    };

    // Helper to add to recently removed items for undo
    function addToRecentlyRemovedItems(uid: string) {
      const cart = getPersistedCartData();
      const item = cart?.items.find((i) => i.uid === uid);
      if (item) {
        setRecentlyRemovedItems((prev) => {
          const index = cart.items.findIndex((i) => i.uid === uid);
          if (index !== -1 && !prev.some((r) => r.item.uid === uid)) {
            // Emit the event for analytics or listeners
            events.emit('cart/product/removed', { item, index });
            return [
              ...prev,
              { item, index, error: undefined, beingRemoved: true },
            ];
          }
          return prev;
        });
      }
    }

    // isExplicitRemoval distinguishes deliberate remove actions (trash button, confirm-delete,
    // out-of-stock removal) from the quantity input reaching 0. Explicit removals bypass the
    // removeOnZero guard so they always work regardless of enableUpdateItemQuantity configuration.
    const handleItemQuantityUpdate = (
      item: CartModel['items'][0],
      quantity: number,
      isExplicitRemoval = false
    ) => {
      // initial values
      handleItemsLoading(item.uid, true);

      // Reset temporary error messages
      setItemErrors(new Map());

      if (
        enableRemoveItem &&
        quantity === 0 &&
        (removeOnZero || isExplicitRemoval)
      ) {
        // Add to recently removed items for undo
        addToRecentlyRemovedItems(item.uid);
        // use callback for removing (original logic)
        updateProductsFromCart([{ uid: item.uid, quantity }])
          .then(() => {
            onItemRemove?.({ item });
          })
          .finally(() => {
            handleItemsLoading(item.uid, false);
            setRecentlyRemovedItems((prev) =>
              prev.map((r) =>
                r.item.uid === item.uid ? { ...r, beingRemoved: false } : r
              )
            );
          })
          .catch((error: any) => {
            console.warn(error);
          });
        // use callback for updating
      } else {
        // quantity === 0 without opt-in: no-op — removeOnZero defaults to false
        if (quantity === 0 && !removeOnZero) {
          handleItemsLoading(item.uid, false);
          return;
        }
        enableUpdateItemQuantity &&
          updateProductsFromCart([{ uid: item.uid, quantity }])
            .then(() => {
              onItemUpdate?.({ item });
            })
            .finally(() => {
              handleItemsLoading(item.uid, false);
            })
            .catch((error: Error) => {
              handleItemsError(item.uid, error.message);
            });
      }
    };

    const handleDismiss = (uid: string) => {
      setRecentlyRemovedItems((prev) => prev.filter((r) => r.item.uid !== uid));
    };

    // Display max items
    const handleMaxItemsView = useCallback(() => {
      setDisplayMaxItems((prev) => !prev);
    }, []);

    useEffect(() => {
      const cartDataEvent = events.on(
        'cart/data',
        (payload: Cart | null) => {
          setCartModelData(payload as CartModel);
          setLoading(!!isLoading);
        },
        { eager: true }
      );

      return () => {
        cartDataEvent?.off();
      };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear recently removed items when undo is disabled
    useEffect(() => {
      if (!undo) {
        setRecentlyRemovedItems([]);
      }
    }, [undo]);

    // Call onItemsErrorsChange whenever itemsErrors changes
    useEffect(() => {
      if (onItemsErrorsChange) {
        onItemsErrorsChange(itemsErrors);
      }
    }, [itemsErrors, onItemsErrorsChange]);

    // Helper functions
    const getImage = (item: CartModel['items'][0], index: number) => {
      if (attributesToHide.includes('image')) {
        return undefined;
      }

      const defaultImageProps: ImageProps = {
        loading: index < 4 ? 'eager' : 'lazy',
        src: item.image.src,
        alt: item.image.alt,
        width: '300',
        height: '300',
        params: { width: 300 },
      };

      const imageComponent = (
        <Image data-testid="cart-list-item-image" {...defaultImageProps} />
      );
      return (
        <Slot
          name="Thumbnail"
          slot={slots?.Thumbnail}
          context={{
            item,
            defaultImageProps,
          }}
        >
          {routeProduct ? (
            <a href={routeProduct(item)}>{imageComponent}</a>
          ) : (
            imageComponent
          )}
        </Slot>
      );
    };

    const getTitle = (item: CartModel['items'][0]) => {
      if (attributesToHide.includes('name')) {
        return undefined;
      }

      const content = routeProduct ? (
        <a href={routeProduct(item)}>{decodeHTMLEntities(item.name)}</a>
      ) : (
        decodeHTMLEntities(item.name)
      );

      if (itemTitleHeadingLevel) {
        const TitleTag = `h${itemTitleHeadingLevel}` as any;
        return (
          <TitleTag
            className="cart-cart-summary-list__item-title"
            data-testid="cart-list-item-title"
          >
            {content}
          </TitleTag>
        );
      }

      return (
        <span
          className="cart-cart-summary-list__item-title"
          data-testid="cart-list-item-title"
        >
          {content}
        </span>
      );
    };

    const getConfiguration = (item: CartModel['items'][0]) => {
      if (attributesToHide.includes('configurations')) {
        return undefined;
      }

      const configuration = {
        ...item.bundleOptions,
        ...item.selectedOptions,
        ...item.customizableOptions,
        ...(item.recipient ? { [dictionary.recipient]: item.recipient } : null),
        ...(item.recipientEmail && item.recipient
          ? {
              [dictionary.recipient]: `${item.recipient} (${item.recipientEmail})`,
            }
          : null),
        ...(item.sender ? { [dictionary.sender]: item.sender } : null),
        ...(item.senderEmail && item.sender
          ? {
              [dictionary.sender]: `${item.sender} (${item.senderEmail})`,
            }
          : {}),
        ...(item.message ? { [dictionary.message]: item.message } : null),
        ...(item.links && item.links.count
          ? item.links.count > 1
            ? {
                [dictionary.files.replace(
                  '{count}',
                  item.links.count.toString()
                )]: item.links.result,
              }
            : {
                [dictionary.file.replace(
                  '{count}',
                  item.links.count.toString()
                )]: item.links.result,
              }
          : null),
      };

      if (Object.keys(configuration).length === 0) {
        return undefined;
      }

      return configuration;
    };

    const getPriceProps = (item: CartModel['items'][0]) => {
      return cartTaxesConfig?.price === 'INCLUDING_TAX'
        ? item.discounted
          ? {
              amount: item.regularPrice.value,
              currency: item.regularPrice.currency,
              style: { font: 'inherit' },
              'data-testid': 'including-tax-item-price',
            }
          : {
              amount: item.taxedPrice?.value,
              currency: item.taxedPrice?.currency,
              style: { font: 'inherit' },
              'data-testid': 'including-tax-item-price',
            }
        : {
            amount: item.regularPrice?.value,
            currency: item.regularPrice?.currency,
            style: { font: 'inherit' },
            'data-testid': 'regular-item-price',
          };
    };

    const getSavingsAmount = (item: CartModel['items'][0]) => {
      return {
        amount: item.savingsAmount?.value,
        currency: item.savingsAmount?.currency,
        style: { font: 'inherit' },
        'data-testid': 'item-savings-amount',
      };
    };

    const getTotalExcludingTax = (item: CartModel['items'][0]) => {
      return cartTaxesConfig?.price === 'INCLUDING_EXCLUDING_TAX' ? (
        <Price
          amount={item.rowTotal.value}
          currency={item.rowTotal.currency}
          data-testid="excluding-tax-total"
          aria-label={dictionary.regularPrice}
        />
      ) : undefined;
    };

    const getTotalPriceProps = (item: CartModel['items'][0]) => {
      const totalProps: PriceProps = {
        'aria-label': dictionary.regularPrice,
      };
      const discountProps: PriceProps | null = item.discounted ? {} : null;

      if (
        ['INCLUDING_TAX', 'INCLUDING_EXCLUDING_TAX'].includes(
          cartTaxesConfig?.price as string
        )
      ) {
        totalProps['amount'] = item.rowTotalIncludingTax?.value;
        totalProps['currency'] = item.rowTotalIncludingTax?.currency;
        totalProps['variant'] = item.discounted ? 'strikethrough' : 'default';
        // @ts-ignore
        totalProps['data-testid'] = 'including-tax-item-total';

        if (discountProps) {
          totalProps['amount'] = item.total?.value;
          totalProps['currency'] = item.total?.currency;

          discountProps['amount'] = item.rowTotalIncludingTax?.value;
          discountProps['currency'] = item.rowTotalIncludingTax?.currency;
          discountProps['sale'] = true;
          discountProps['aria-label'] = dictionary.discountedPrice;
          // @ts-ignore
          discountProps['data-testid'] = 'discount-total';
        }
      } else {
        totalProps['amount'] = item.total?.value;
        totalProps['currency'] = item.total?.currency;
        totalProps['variant'] = item.discounted ? 'strikethrough' : 'default';
        // @ts-ignore
        totalProps['data-testid'] = 'regular-item-total';

        if (discountProps) {
          discountProps['amount'] = item.discountedTotal?.value;
          discountProps['currency'] = item.discountedTotal?.currency;
          discountProps['sale'] = true;
          discountProps['aria-label'] = dictionary.regularPrice;
          // @ts-ignore
          discountProps['data-testid'] = 'discount-total';
        }
      }

      return {
        totalProps,
        discountProps,
      };
    };

    const getWarning = (item: CartModel['items'][0]) => {
      if (attributesToHide.includes('warning')) {
        return undefined;
      }

      const error = itemsErrors.get(item.uid);
      const quantityError = itemsErrors
        .get(item.uid)
        ?.includes('The requested qty is not available');
      const itemIsLoading = itemsLoading.has(item.uid);

      const insufficientQuantityText =
        item.insufficientQuantity && item.stockLevel
          ? item.stockLevel === 'noNumber'
            ? dictionary.insufficientQuantityGeneral
            : dictionary.insufficientQuantity
                .replace('{inventory}', item.stockLevel?.toString())
                .replace('{count}', item.quantity.toString())
          : '';

      const lowInventoryText =
        item.lowInventory &&
        item.onlyXLeftInStock &&
        dictionary.lowInventory.replace(
          '{count}',
          item.onlyXLeftInStock?.toString()
        );

      const notAvailableQuantity =
        !item.outOfStock && error && quantityError
          ? dictionary.notAvailableMessage
          : error;

      return !itemIsLoading &&
        (error || item.insufficientQuantity || item.lowInventory) ? (
        <span data-testid="item-warning">
          <Icon source={WarningWithCircle} size={'16'} />
          {notAvailableQuantity || insufficientQuantityText || lowInventoryText}
        </span>
      ) : undefined;
    };

    const getAlert = (item: CartModel['items'][0]) => {
      if (attributesToHide?.includes('alert')) {
        return undefined;
      }
      const itemIsLoading = itemsLoading.has(item.uid);

      return !itemIsLoading && item.outOfStock ? (
        <span data-testid="item-alert">
          <Icon source={OrderError} size={'16'} />
          {dictionary.outOfStockAlert}
        </span>
      ) : undefined;
    };

    const getProductAttributes = (item: CartModel['items'][0]) => {
      return (
        <Slot
          name="ProductAttributes"
          slot={slots?.ProductAttributes}
          context={{
            item,
          }}
        />
      );
    };

    const getSku = (item: CartModel['items'][0]) => {
      if (attributesToHide.includes('sku')) {
        return undefined;
      }
      return <span data-testid="cart-list-item-sku">{item.sku}</span>;
    };

    const getFooter = (item: CartModel['items'][0]) => {
      return (
        <Slot
          name="Footer"
          slot={slots?.Footer}
          context={{ item, handleItemsLoading, handleItemsError, onItemUpdate }}
        />
      );
    };

    const getRowTotalFooter = (item: CartModel['items'][0]) => {
      return (
        <Slot
          name="RowTotalFooter"
          slot={slots?.RowTotalFooter}
          context={{ item }}
        />
      );
    };

    const getRemoveContent = (item: CartModel['items'][0]) => {
      if (!enableRemoveItem) {
        return undefined;
      }

      return (
        <Slot
          name="ItemRemoveAction"
          slot={slots?.ItemRemoveAction}
          context={{
            item,
            enableRemoveItem,
            handleItemQuantityUpdate,
            handleItemsError,
            handleItemsLoading,
            onItemUpdate,
            itemsLoading,
          }}
        >
          {/* Default remove button */}
          <Button
            data-testid="cart-item-remove-button"
            className={classes(['dropin-cart-item__remove'])}
            variant="tertiary"
            onClick={() => handlePendingDelete(item)}
            disabled={itemsLoading.has(item.uid)}
            icon={
              <Icon
                data-testid="cart-item-remove-icon"
                source={Trash}
                size="24"
                stroke="2"
                viewBox="0 0 24 24"
                aria-label={
                  dictionary.remove?.replace(
                    '{product}',
                    decodeHTMLEntities(item.name)
                  ) || dictionary.removeDefault
                }
              />
            }
          />
        </Slot>
      );
    };

    const getQuantityContent = (item: CartModel['items'][0]) => {
      if (attributesToHide.includes('quantity')) {
        return undefined;
      }

      if (!enableUpdateItemQuantity) {
        return undefined;
      }

      return (
        <Slot
          name="ItemQuantity"
          slot={slots?.ItemQuantity}
          context={{
            item,
            enableUpdateItemQuantity,
            handleItemQuantityUpdate,
            itemsLoading,
            handleItemsError,
            handleItemsLoading,
            onItemUpdate,
          }}
        >
          <Incrementer
            value={item.quantity}
            min={removeOnZero ? 0 : 1}
            onValue={(value) => handleItemQuantityUpdate(item, Number(value))}
            name="quantity"
            disabled={itemsLoading.has(item.uid)}
            aria-label={dictionary.quantity}
          />
        </Slot>
      );
    };

    // Re-usable function to get a filtered list of cart items
    const getCartItemEntries = (
      filterCallback: (item: CartModel['items'][0]) => boolean
    ) => {
      return cartModelData?.totalQuantity
        ? cartModelData.items.filter(filterCallback).map((item, index) => {
            const { totalProps, discountProps } = getTotalPriceProps(item);

            const defaultCartItem = (
              <CartItem
                updating={itemsLoading?.has(item.uid)}
                loading={loading}
                key={item.uid}
                data-testid={`cart-list-item-entry-${item.uid}`}
                image={getImage(item, index)}
                title={
                  <Slot
                    name="ItemTitle"
                    slot={slots?.ItemTitle}
                    context={{ item }}
                  >
                    {getTitle(item)}
                  </Slot>
                }
                sku={
                  attributesToHide.includes('sku') ? undefined : (
                    <Slot
                      name="ItemSku"
                      slot={slots?.ItemSku}
                      context={{ item }}
                    >
                      {getSku(item)}
                    </Slot>
                  )
                }
                price={
                  attributesToHide.includes('price') ? undefined : (
                    <Slot
                      name="ItemPrice"
                      slot={slots?.ItemPrice}
                      context={{ item }}
                      slotTag="span"
                    >
                      <Price {...getPriceProps(item)} />
                    </Slot>
                  )
                }
                quantity={
                  attributesToHide.includes('quantity')
                    ? undefined
                    : item.quantity
                }
                quantityContent={getQuantityContent(item)}
                total={
                  <>
                    {attributesToHide.includes('total') ? undefined : (
                      <Slot
                        name="ItemTotal"
                        slot={slots?.ItemTotal}
                        context={{ item }}
                      >
                        <Price {...totalProps} />
                      </Slot>
                    )}
                    {attributesToHide.includes('totalDiscount')
                      ? undefined
                      : discountProps && (
                          <Slot
                            name="ItemTotal"
                            slot={slots?.ItemTotal}
                            context={{ item }}
                          >
                            <Price {...discountProps} />
                          </Slot>
                        )}
                  </>
                }
                attributes={getProductAttributes(item)}
                configurations={getConfiguration(item)}
                totalExcludingTax={
                  attributesToHide.includes('totalExcludingTax')
                    ? undefined
                    : getTotalExcludingTax(item)
                }
                taxIncluded={cartTaxesConfig?.price === 'INCLUDING_TAX'}
                taxExcluded={
                  !attributesToHide.includes('totalExcludingTax') &&
                  cartTaxesConfig?.price === 'INCLUDING_EXCLUDING_TAX'
                }
                warning={getWarning(item)}
                alert={getAlert(item)}
                quantityType={quantityType}
                dropdownOptions={dropdownOptions}
                onQuantity={
                  enableUpdateItemQuantity && !itemsLoading.has(item.uid)
                    ? (newQuantity) => {
                        handleItemQuantityUpdate(item, newQuantity);
                      }
                    : undefined
                }
                onRemove={
                  enableRemoveItem && !itemsLoading.has(item.uid)
                    ? () => handlePendingDelete(item)
                    : undefined
                }
                discount={
                  showDiscount && item.discounted && item.discountPercentage ? (
                    <div data-testid="item-discount-percent">
                      {dictionary.discountPercent.replace(
                        '{discount}',
                        item.discountPercentage?.toString() ?? ''
                      )}
                    </div>
                  ) : undefined
                }
                savings={
                  showSavings && item.discounted && item.savingsAmount ? (
                    <div>
                      <span>
                        <Price {...getSavingsAmount(item)} />
                      </span>
                      &nbsp;
                      {dictionary.savingsAmount}
                    </div>
                  ) : undefined
                }
                rowTotalFooter={getRowTotalFooter(item)}
                footer={getFooter(item)}
                removeContent={getRemoveContent(item)}
              />
            );

            return (
              <Slot
                key={item.uid}
                name="CartItem"
                slot={slots?.CartItem}
                context={{
                  item,
                  index,
                  enableUpdateItemQuantity,
                  enableRemoveItem,
                  itemsLoading,
                  loading,
                  attributesToHide,
                  cartTaxesConfig,
                  quantityType,
                  dropdownOptions,
                  showDiscount,
                  showSavings,
                  dictionary,
                  getImage,
                  getTitle,
                  getSku,
                  getPriceProps,
                  getTotalPriceProps,
                  getProductAttributes,
                  getConfiguration,
                  getTotalExcludingTax,
                  getWarning,
                  getAlert,
                  getRowTotalFooter,
                  getFooter,
                  getSavingsAmount,
                }}
              >
                {defaultCartItem}
              </Slot>
            );
          })
        : null;
    };

    const HeadingTag = `h${headingLevel}` as any;

    const heading = (
      <Slot
        name="Heading"
        slot={slots?.Heading}
        context={{
          count: cartModelData?.totalQuantity,
        }}
      >
        <HeadingTag
          data-testid="default-cart-heading"
          className="cart-cart-summary-list__heading-text"
        >
          {dictionary.heading.replace(
            '({count})',
            cartModelData?.totalQuantity
              ? `(${cartModelData?.totalQuantity.toString()})`
              : ''
          )}
        </HeadingTag>
      </Slot>
    );

    // Heading Section text for screen reader
    const headingSection = heading.props.children.props.children;

    // Out of Stock Items
    const handleRemoveOutOfStockItems = () => {
      const outOfStockItems = cartModelData?.items.filter(
        (item) => item.outOfStock
      );

      outOfStockItems?.forEach((item) => {
        handleItemQuantityUpdate(item, 0, true);
      });
    };

    const outOfStockEntries = getCartItemEntries(
      (item) => item.outOfStock || item.insufficientQuantity || false
    );

    const outOfStockMessage = cartModelData?.hasOutOfStockItems ? (
      <InLineAlert
        data-testid="cart-out-of-stock-message"
        icon={<Icon source={OrderError} size={'16'} />}
        itemList={
          <CartList
            className="cart-cart-summary-list__out-of-stock-items"
            data-testid="out-of-stock-cart-items"
            aria-hidden={includeOutOfStockItems || undefined}
          >
            {outOfStockEntries}
          </CartList>
        }
        type="warning"
        heading={dictionary.outOfStockHeading}
        description={dictionary.outOfStockDescription}
        variant="primary"
        actionButtonPosition="bottom"
        additionalActions={
          cartModelData?.hasFullyOutOfStockItems && enableRemoveItem
            ? [
                {
                  label: dictionary.removeAction,
                  onClick: handleRemoveOutOfStockItems,
                },
              ]
            : undefined
        }
      />
    ) : undefined;

    // Main Product Entries
    const mainProductEntries = getCartItemEntries(
      includeOutOfStockItems
        ? () => true
        : (item) => !item.outOfStock && !item.insufficientQuantity
    );

    const numOfVisibleItems = displayMaxItems
      ? Math.max(maxItems || 5, 5)
      : Math.min(cartModelData?.totalQuantity || 5, 5);

    const hasHiddenItems = cartModelData?.totalQuantity > numOfVisibleItems;

    const canDisplayMoreItems =
      hasHiddenItems && !displayMaxItems && numOfVisibleItems != maxItems;

    const footerActions =
      cartModelData?.totalQuantity && mainProductEntries ? (
        <Slot
          name="Footer"
          slot={slots?.CartSummaryFooter}
          context={{ displayMaxItems, routeCart }}
          data-testid="cart-cart-summary-footer-slot"
          className="cart-cart-summary-footer"
        >
          {hasHiddenItems ? (
            canDisplayMoreItems ? (
              <Button
                className="cart-cart-summary-list-footer__action"
                onClick={handleMaxItemsView}
                data-testid="view-more-items-button"
                variant="tertiary"
              >
                {dictionary.viewMore}
              </Button>
            ) : (
              routeCart && (
                <Button
                  className="cart-cart-summary-list-footer__action"
                  href={routeCart()}
                  variant="tertiary"
                  data-testid="view-cart-or-less-items-button"
                >
                  {dictionary.viewAll}
                </Button>
              )
            )
          ) : (
            routeCart && (
              <Button
                className="cart-cart-summary-list-footer__action"
                href={routeCart()}
                variant="tertiary"
                data-testid="view-cart-button"
              >
                {dictionary.viewAll}
              </Button>
            )
          )}
        </Slot>
      ) : null;

    const renderCartListWithUndoBanners = () => {
      if (!cartModelData?.items) return null;
      const entries: (VNode | null)[] = [];
      const items = cartModelData.items;
      const UndoBanner = slots?.UndoBanner;

      // Calculate how many items to show (same logic as original)
      const numItemsToShow = displayMaxItems
        ? Math.max(!maxItems ? cartModelData?.totalQuantity : maxItems, 5)
        : Math.min(maxItems ?? 5, 5);

      // Only process items up to the limit
      for (let i = 0; i <= numItemsToShow; i++) {
        // Render undo banners for this index only if undo is enabled
        if (undo) {
          recentlyRemovedItems
            .filter((r) => r.index === i)
            .forEach(({ item, error, beingRemoved }) => {
              const defaultUndoBanner = (
                <InLineAlert
                  key={`undo-banner-${item.uid}`}
                  type={error ? 'error' : 'info'}
                  heading={
                    beingRemoved
                      ? dictionary.itemBeingRemoved?.replace(
                          '{product}',
                          decodeHTMLEntities(item.name)
                        )
                      : dictionary.itemRemoved?.replace(
                          '{product}',
                          decodeHTMLEntities(item.name)
                        )
                  }
                  description={error || dictionary.itemRemovedDescription}
                  variant="primary"
                  actionButtonPosition="bottom"
                  additionalActions={
                    beingRemoved
                      ? [] // Hide buttons while removing
                      : [
                          {
                            label: dictionary.undoAction,
                            onClick: () => handleUndo(item.uid),
                            'aria-label': `${
                              dictionary.undoAction
                            } remove ${decodeHTMLEntities(item.name)}`,
                          },
                          {
                            label: dictionary.dismissAction,
                            onClick: () => handleDismiss(item.uid),
                            'aria-label': `${
                              dictionary.dismissAction
                            } undo for ${decodeHTMLEntities(item.name)}`,
                          },
                        ]
                  }
                />
              );

              entries.push(
                UndoBanner ? (
                  <Slot
                    name="UndoBanner"
                    slot={UndoBanner}
                    context={{
                      item,
                      loading,
                      error,
                      onUndo: () => handleUndo(item.uid),
                      onDismiss: () => handleDismiss(item.uid),
                    }}
                    key={`undo-banner-${item.uid}`}
                    className="cart-summary-list__banner cart-summary-list__banner--undo"
                  >
                    {defaultUndoBanner}
                  </Slot>
                ) : (
                  <div
                    key={`undo-banner-${item.uid}`}
                    className="cart-summary-list__banner cart-summary-list__banner--undo"
                  >
                    {defaultUndoBanner}
                  </div>
                )
              );
            });
        }
        // Render cart item if not at the end and within the slice limit
        if (i < numItemsToShow && i < items.length) {
          const currentItem = items[i];

          // Render confirm delete banner before item when awaiting confirmation
          if (confirmBeforeDelete && pendingDeleteUids.has(currentItem.uid)) {
            const onConfirm = () => {
              setPendingDeleteUids((prev) => {
                const next = new Set(prev);
                next.delete(currentItem.uid);
                return next;
              });
              handleItemQuantityUpdate(currentItem, 0, true);
            };
            const onCancel = () => {
              setPendingDeleteUids((prev) => {
                const next = new Set(prev);
                next.delete(currentItem.uid);
                return next;
              });
            };
            const defaultConfirmDeleteBanner = (
              <InLineAlert
                key={`confirm-delete-banner-${currentItem.uid}`}
                type="warning"
                heading={dictionary.confirmDeleteHeading?.replace(
                  '{product}',
                  decodeHTMLEntities(currentItem.name)
                )}
                variant="primary"
                actionButtonPosition="bottom"
                additionalActions={[
                  { label: dictionary.confirmAction, onClick: onConfirm },
                  { label: dictionary.cancelAction, onClick: onCancel },
                ]}
              />
            );
            entries.push(
              <Slot
                name="ConfirmDeleteBanner"
                slot={slots?.ConfirmDeleteBanner}
                context={{
                  item: currentItem,
                  loading: itemsLoading.has(currentItem.uid),
                  onConfirm,
                  onCancel,
                }}
                key={`confirm-delete-banner-${currentItem.uid}`}
                className="cart-summary-list__banner cart-summary-list__banner--confirm-delete"
              >
                {defaultConfirmDeleteBanner}
              </Slot>
            );
          }

          entries.push(mainProductEntries?.[i] || null);
        }
      }
      return <CartList data-testid="cart-list">{entries}</CartList>;
    };
    // Undo handler implementation
    const handleUndo = useCallback(
      async (uid: string) => {
        setRecentlyRemovedItems((prev) =>
          prev.map((r) =>
            r.item.uid === uid ? { ...r, loading: true, error: undefined } : r
          )
        );
        const removed = recentlyRemovedItems.find((r) => r.item.uid === uid);
        if (!removed) return;
        try {
          await addProductsToCart([
            {
              sku: removed.item.sku,
              parentSku: removed.item.topLevelSku,
              quantity: removed.item.quantity,
              optionsUIDs:
                removed.item.bundleOptionsUIDs || // Use bundle option UIDs for bundle products
                (removed.item.selectedOptionsUIDs
                  ? Object.values(removed.item.selectedOptionsUIDs)
                  : undefined),
              enteredOptions: removed.item.customizableOptions
                ? Object.entries(removed.item.customizableOptions).map(
                    ([uid, value]) => ({ uid, value })
                  )
                : undefined,
            },
          ]);
          setRecentlyRemovedItems((prev) =>
            prev.filter((r) => r.item.uid !== uid)
          );
        } catch (error: any) {
          setRecentlyRemovedItems((prev) =>
            prev.map((r) =>
              r.item.uid === uid
                ? { ...r, error: error?.message || 'Failed to restore item.' }
                : r
            )
          );
        }
      },
      [recentlyRemovedItems]
    );

    // Expose handleUndo to parent components via ref (for testing)
    useImperativeHandle(
      ref,
      () => ({
        handleUndo,
      }),
      [handleUndo]
    );

    // Replace the emptyCart definition with conditional rendering for undo banners
    const emptyCart = (
      <>
        {undo && recentlyRemovedItems.length > 0 ? (
          renderCartListWithUndoBanners()
        ) : (
          <Slot name="EmptyCart" slot={slots?.EmptyCart} context={{}}>
            <EmptyCart
              data-testid="empty-cart"
              ctaLinkURL={routeEmptyCartCTA?.()}
            />
          </Slot>
        )}
      </>
    );

    // Choose between original product entries and undo/confirm-delete banner version
    const simpleCartList = (
      <CartList data-testid="cart-list">
        {mainProductEntries?.slice(
          0,
          displayMaxItems
            ? Math.max(!maxItems ? cartModelData?.totalQuantity : maxItems, 5)
            : Math.min(maxItems ?? 5, 5)
        )}
      </CartList>
    );

    const productEntries =
      cartModelData?.totalQuantity || (undo && recentlyRemovedItems.length > 0)
        ? undo || confirmBeforeDelete
          ? recentlyRemovedItems.length > 0 || pendingDeleteUids.size > 0
            ? renderCartListWithUndoBanners()
            : simpleCartList
          : simpleCartList
        : null;

    return accordion ? (
      <Accordion
        data-testid="cart-summary-list-accordion"
        className={classes([
          `cart-cart-summary-list-accordion`,
          `cart-cart-summary-list__background--${variant}`,
        ])}
        iconOpen={ChevronRight}
        iconClose={ChevronDown}
      >
        <AccordionSection
          title={headingSection}
          data-testid="cart-summary-list-accordion__section"
          open={true}
          renderContentWhenClosed={true}
        >
          <CartSummaryListComponent
            {...props}
            aria-expanded={true}
            aria-label={'TEST'}
            className={classes([
              'cart-cart-summary-list-accordion__list',
              [
                'cart-cart-summary-list--include-out-of-stock',
                includeOutOfStockItems,
              ],
            ])}
            loading={loading}
            footer={
              hideFooter
                ? undefined
                : footerActions || (routeCart ? footerActions : undefined)
            }
            emptyCart={emptyCart}
            products={productEntries}
            outOfStockMessage={outOfStockMessage}
            variant={variant}
          />
        </AccordionSection>
      </Accordion>
    ) : (
      <CartSummaryListComponent
        {...props}
        className={classes([
          [
            'cart-cart-summary-list--include-out-of-stock',
            includeOutOfStockItems,
          ],
        ])}
        heading={hideHeading ? undefined : heading}
        footer={
          hideFooter
            ? undefined
            : footerActions || (routeCart ? footerActions : undefined)
        }
        loading={loading}
        emptyCart={emptyCart}
        products={productEntries}
        outOfStockMessage={outOfStockMessage}
        variant={variant}
      />
    );
  }
);

CartSummaryList.getInitialData = async function () {
  return getPersistedCartData();
};
